import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import {
  normalizeRichTextHtml,
  normalizeRichTextHtmlForEditorSync,
  plainTextToRichTextHtml,
  richTextHtmlToPlainText,
  type RichTextOptions,
} from '@/lib/rich-text'
import { cn } from '@/lib/utils'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LinkNode, TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link'
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
  $isListNode,
  type ListType,
} from '@lexical/list'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $createParagraphNode,
  $isElementNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type BaseSelection,
  type EditorState,
  type EditorThemeClasses,
  type LexicalEditor,
  type LexicalNode,
  type ParagraphNode,
  type RootNode,
} from 'lexical'
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Underline, Unlink } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Typography } from '@/components/ui/typography'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Separator } from '../ui/separator'
import { ToggleButton, type ToggleButtonProps } from '../ui/toggle-button'
import { RichTextContent } from '../ui/rich-text-content'
import FieldInfo from './FieldInfo'
import { Label } from '../ui/label'

const editorTheme: EditorThemeClasses = {
  paragraph: 'mb-3 last:mb-0',
  link: 'text-interaction underline underline-offset-2',
  text: {
    bold: 'font-semibold',
    italic: 'italic',
    underline: 'underline underline-offset-2',
  },
  list: {
    ul: 'my-3 list-disc pl-5',
    ol: 'my-3 list-decimal pl-5',
    listitem: 'mt-1',
  },
}

function getSelectedLinkUrl(selection: BaseSelection | null): string | null {
  if (!$isRangeSelection(selection)) {
    return null
  }

  let node: LexicalNode | null = selection.anchor.getNode()

  while (node) {
    if ($isLinkNode(node)) {
      return node.getURL()
    }

    node = node.getParent()
  }

  return null
}

function getSelectedListType(selection: BaseSelection | null): ListType | null {
  if (!$isRangeSelection(selection)) {
    return null
  }

  let node: LexicalNode | null = selection.anchor.getNode()

  while (node) {
    if ($isListNode(node)) {
      return node.getListType()
    }

    node = node.getParent()
  }

  return null
}

function normalizeEditorHtmlForLexical(html: string | null): string | null {
  if (typeof html !== 'string' || !html.trim() || typeof DOMParser === 'undefined') {
    return html
  }

  const sourceDocument = new DOMParser().parseFromString(html, 'text/html')

  if (!sourceDocument?.body) {
    return html
  }

  const outputDocument = sourceDocument.implementation.createHTMLDocument('')
  const root = outputDocument.createElement('div')
  let paragraph: HTMLParagraphElement | null = null

  const flushParagraph = () => {
    if (paragraph?.childNodes.length) {
      root.append(paragraph)
    }

    paragraph = null
  }

  for (const childNode of Array.from(sourceDocument.body.childNodes)) {
    if (
      childNode instanceof Element &&
      ['p', 'ul', 'ol', 'li'].includes(childNode.tagName.toLowerCase())
    ) {
      flushParagraph()
      root.append(outputDocument.importNode(childNode, true))
      continue
    }

    if (childNode.nodeType === Node.TEXT_NODE && !(childNode.textContent || '').trim()) {
      continue
    }

    if (!paragraph) {
      paragraph = outputDocument.createElement('p')
    }

    paragraph.append(outputDocument.importNode(childNode, true))
  }

  flushParagraph()

  return root.innerHTML.trim() || html
}

function getBootstrapPlainTextValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function getComparablePlainTextValue(value: unknown): string {
  return getBootstrapPlainTextValue(value) ?? ''
}

function getRawPlainTextValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function normalizeRichTextFormValue(
  value: unknown,
  options: RichTextOptions = {}
): string | null {
  return normalizeEditorHtmlForLexical(normalizeRichTextHtml(value, options))
}

function normalizePlainTextBootstrapHtml(value: string, options: RichTextOptions): string | null {
  return normalizeEditorHtmlForLexical(plainTextToRichTextHtml(value, options))
}

function getEditorSyncValue(html: string | null): string {
  return typeof html === 'string' ? html.trim() : ''
}

export type RichTextInputSnapshotMode =
  | 'html'
  | 'plain_text_bootstrap'
  | 'fallback_html'
  | 'fallback_plain_text_bootstrap'
  | 'empty'

export type RichTextInputSnapshot = {
  formHtmlValue: string | null
  rawPlainTextValue: string
  comparablePlainTextValue: string
  editorComparablePlainTextValue?: string
  editorHtmlValue: string
  mode: RichTextInputSnapshotMode
}

export type RichTextInputSnapshotOptions = RichTextOptions & {
  htmlValue: unknown
  plainTextValue: unknown
  fallbackHtmlValue?: unknown
  fallbackPlainTextValue?: unknown
}

export function buildRichTextInputSnapshot({
  htmlValue,
  plainTextValue,
  fallbackHtmlValue = undefined,
  fallbackPlainTextValue = undefined,
  preserveSpaces = false,
}: RichTextInputSnapshotOptions): RichTextInputSnapshot {
  const options = { preserveSpaces }
  const formHtmlValue = normalizeRichTextFormValue(htmlValue, options)
  const rawPlainTextValue = getRawPlainTextValue(plainTextValue)
  const comparablePlainTextValue = getComparablePlainTextValue(rawPlainTextValue)
  const bootstrapHtmlValue =
    formHtmlValue === null ? normalizePlainTextBootstrapHtml(rawPlainTextValue, options) : null

  if (formHtmlValue !== null) {
    return {
      formHtmlValue,
      rawPlainTextValue,
      comparablePlainTextValue,
      editorComparablePlainTextValue: comparablePlainTextValue,
      editorHtmlValue: formHtmlValue,
      mode: 'html',
    }
  }

  if (bootstrapHtmlValue) {
    return {
      formHtmlValue,
      rawPlainTextValue,
      comparablePlainTextValue,
      editorComparablePlainTextValue: comparablePlainTextValue,
      editorHtmlValue: bootstrapHtmlValue,
      mode: 'plain_text_bootstrap',
    }
  }

  const fallbackFormHtmlValue = normalizeRichTextFormValue(fallbackHtmlValue, options)
  const fallbackRawPlainTextValue = getRawPlainTextValue(fallbackPlainTextValue)
  const fallbackComparablePlainTextValue = getComparablePlainTextValue(fallbackRawPlainTextValue)
  const fallbackBootstrapHtmlValue =
    fallbackFormHtmlValue === null
      ? normalizePlainTextBootstrapHtml(fallbackRawPlainTextValue, options)
      : null

  return {
    formHtmlValue,
    rawPlainTextValue,
    comparablePlainTextValue,
    editorComparablePlainTextValue:
      fallbackFormHtmlValue !== null || fallbackBootstrapHtmlValue
        ? fallbackComparablePlainTextValue
        : comparablePlainTextValue,
    editorHtmlValue: fallbackFormHtmlValue ?? fallbackBootstrapHtmlValue ?? '',
    mode:
      fallbackFormHtmlValue !== null
        ? 'fallback_html'
        : fallbackBootstrapHtmlValue
          ? 'fallback_plain_text_bootstrap'
          : 'empty',
  }
}

function buildRichTextInputEditorSnapshot(
  generatedHtml: string | null,
  options: RichTextOptions
): RichTextInputSnapshot {
  const nextHtml = normalizeRichTextHtml(generatedHtml, options)
  const formHtmlValue = normalizeRichTextFormValue(nextHtml, options)
  // `richTextHtmlToPlainText` returns a string or null, so this matches `?? ''`.
  const rawPlainTextValue = getRawPlainTextValue(richTextHtmlToPlainText(nextHtml, options))

  return {
    formHtmlValue,
    rawPlainTextValue,
    comparablePlainTextValue: getComparablePlainTextValue(rawPlainTextValue),
    editorHtmlValue: formHtmlValue ?? '',
    mode: formHtmlValue !== null ? 'html' : 'empty',
  }
}

function isBootstrapEditorEcho(
  committedSnapshot: RichTextInputSnapshot,
  nextSnapshot: RichTextInputSnapshot
): boolean {
  return (
    ['plain_text_bootstrap', 'fallback_html', 'fallback_plain_text_bootstrap'].includes(
      committedSnapshot?.mode
    ) &&
    committedSnapshot.editorHtmlValue === nextSnapshot.editorHtmlValue &&
    (committedSnapshot.editorComparablePlainTextValue ??
      committedSnapshot.comparablePlainTextValue) === nextSnapshot.comparablePlainTextValue
  )
}

export type RichTextInputChange = {
  isBootstrapEcho: boolean
  nextSnapshot: RichTextInputSnapshot
  shouldUpdateHtml: boolean
  nextHtmlValue: string | null
  shouldUpdatePlainText: boolean
  nextPlainTextValue: string
}

export type ResolveRichTextInputChangeOptions = RichTextOptions & {
  committedSnapshot: RichTextInputSnapshot
  generatedHtml: string | null
}

export function resolveRichTextInputChange({
  committedSnapshot,
  generatedHtml,
  preserveSpaces = false,
}: ResolveRichTextInputChangeOptions): RichTextInputChange {
  const nextSnapshot = buildRichTextInputEditorSnapshot(generatedHtml, { preserveSpaces })
  const isBootstrapEcho = isBootstrapEditorEcho(committedSnapshot, nextSnapshot)

  return {
    isBootstrapEcho,
    nextSnapshot: isBootstrapEcho ? committedSnapshot : nextSnapshot,
    shouldUpdateHtml:
      !isBootstrapEcho && committedSnapshot.formHtmlValue !== nextSnapshot.formHtmlValue,
    nextHtmlValue: nextSnapshot.formHtmlValue,
    shouldUpdatePlainText:
      !isBootstrapEcho && committedSnapshot.rawPlainTextValue !== nextSnapshot.rawPlainTextValue,
    nextPlainTextValue: nextSnapshot.rawPlainTextValue,
  }
}

export type ShouldResyncEditorAfterSanitizationOptions = RichTextOptions & {
  generatedHtml: string | null
  normalizedHtml: string | null
  isApplyingExternalHtml?: boolean
}

export function shouldResyncEditorAfterSanitization({
  generatedHtml,
  normalizedHtml,
  isApplyingExternalHtml = false,
  preserveSpaces = false,
}: ShouldResyncEditorAfterSanitizationOptions): boolean {
  const options = { preserveSpaces }

  return (
    !isApplyingExternalHtml &&
    getEditorSyncValue(normalizeRichTextHtmlForEditorSync(generatedHtml, options)) !==
      getEditorSyncValue(normalizeRichTextHtmlForEditorSync(normalizedHtml, options))
  )
}

function withExternalEditorSync(
  isApplyingExternalHtmlRef: RefObject<boolean>,
  runSync: () => void
) {
  isApplyingExternalHtmlRef.current = true
  runSync()

  const releaseExternalSync = () => {
    isApplyingExternalHtmlRef.current = false
  }

  if (typeof queueMicrotask === 'function') {
    queueMicrotask(releaseExternalSync)
    return
  }

  void Promise.resolve().then(releaseExternalSync)
}

function replaceEditorHtml(editor: LexicalEditor, nextHtml: string) {
  editor.update(() => {
    const root = $getRoot()
    root.clear()

    if (nextHtml && typeof DOMParser !== 'undefined') {
      const document = new DOMParser().parseFromString(nextHtml, 'text/html')
      const nodes = $generateNodesFromDOM(editor, document)

      appendGeneratedNodesToRoot(root, nodes)
    }

    if (root.getChildrenSize() === 0) {
      root.append($createParagraphNode())
    }
  })
}

function appendGeneratedNodesToRoot(root: RootNode, nodes: LexicalNode[]) {
  let paragraph: ParagraphNode | null = null

  const flushParagraph = () => {
    if (paragraph && paragraph.getChildrenSize() > 0) {
      root.append(paragraph)
    }

    paragraph = null
  }

  for (const node of nodes) {
    if ($isElementNode(node) && !node.isInline()) {
      flushParagraph()
      root.append(node)
      continue
    }

    if (!paragraph) {
      paragraph = $createParagraphNode()
    }

    paragraph.append(node)
  }

  flushParagraph()
}

type ToolbarButtonProps = Omit<ToggleButtonProps, 'pressed'> & {
  active?: boolean
}

function ToolbarButton({ active = false, children, className = '', ...props }: ToolbarButtonProps) {
  return (
    <ToggleButton
      pressed={active}
      variant="ghost"
      pressedVariant="secondary"
      size="xs"
      className={cn('h-8 px-2.5', className)}
      {...props}
    >
      {children}
    </ToggleButton>
  )
}

function ReadOnlyRichTextToolbar() {
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border/80 bg-muted/85 px-2 py-2 text-muted-foreground dark:bg-background/20">
      <ToolbarButton aria-label="Bold" disabled className="cursor-not-allowed opacity-60">
        <Bold />
      </ToolbarButton>
      <ToolbarButton aria-label="Italic" disabled className="cursor-not-allowed opacity-60">
        <Italic />
      </ToolbarButton>
      <ToolbarButton aria-label="Underline" disabled className="cursor-not-allowed opacity-60">
        <Underline />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-6 opacity-50" />

      <ToolbarButton aria-label="Bulleted list" disabled className="cursor-not-allowed opacity-60">
        <List />
      </ToolbarButton>
      <ToolbarButton aria-label="Numbered list" disabled className="cursor-not-allowed opacity-60">
        <ListOrdered />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-6 opacity-50" />

      <ToolbarButton aria-label="Link" disabled className="cursor-not-allowed opacity-60">
        <LinkIcon />
      </ToolbarButton>
    </div>
  )
}

type ToolbarFormats = {
  bold: boolean
  italic: boolean
  underline: boolean
  listType: ListType | null
  linkUrl: string | null
}

function RichTextToolbar({ inputId }: { inputId: string }) {
  const [editor] = useLexicalComposerContext()
  const [formats, setFormats] = useState<ToolbarFormats>({
    bold: false,
    italic: false,
    underline: false,
    listType: null,
    linkUrl: null,
  })
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const linkInputId = `${inputId}-link`

  useEffect(() => {
    const updateToolbarState = (editorState: EditorState) => {
      editorState.read(() => {
        const selection = $getSelection()

        if (!$isRangeSelection(selection)) {
          setFormats({
            bold: false,
            italic: false,
            underline: false,
            listType: null,
            linkUrl: null,
          })

          return
        }

        setFormats({
          bold: selection.hasFormat('bold'),
          italic: selection.hasFormat('italic'),
          underline: selection.hasFormat('underline'),
          listType: getSelectedListType(selection),
          linkUrl: getSelectedLinkUrl(selection),
        })
      })
    }

    updateToolbarState(editor.getEditorState())

    const unregisterSelectionChange = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbarState(editor.getEditorState())
        return false
      },
      COMMAND_PRIORITY_LOW
    )

    const unregisterUpdate = editor.registerUpdateListener(({ editorState }) => {
      updateToolbarState(editorState)
    })

    return () => {
      unregisterSelectionChange()
      unregisterUpdate()
    }
  }, [editor])

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/70 dark:bg-background/30 px-2 py-2">
      <ToolbarButton
        active={formats.bold}
        aria-label="Bold"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
      >
        <Bold />
      </ToolbarButton>
      <ToolbarButton
        active={formats.italic}
        aria-label="Italic"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
      >
        <Italic />
      </ToolbarButton>
      <ToolbarButton
        active={formats.underline}
        aria-label="Underline"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
      >
        <Underline />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        active={formats.listType === 'bullet'}
        aria-label="Bulleted list"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() =>
          editor.dispatchCommand(
            formats.listType === 'bullet' ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
            undefined
          )
        }
      >
        <List />
      </ToolbarButton>
      <ToolbarButton
        active={formats.listType === 'number'}
        aria-label="Numbered list"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() =>
          editor.dispatchCommand(
            formats.listType === 'number' ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
            undefined
          )
        }
      >
        <ListOrdered />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <Popover
        open={isLinkPopoverOpen}
        onOpenChange={(open) => {
          setIsLinkPopoverOpen(open)

          if (open) {
            setLinkValue(formats.linkUrl || '')
          }
        }}
      >
        <PopoverTrigger asChild>
          <ToolbarButton
            active={Boolean(formats.linkUrl)}
            aria-label="Link"
            onMouseDown={(event) => event.preventDefault()}
          >
            <LinkIcon />
          </ToolbarButton>
        </PopoverTrigger>
        <PopoverContent align="start" size="large" className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor={linkInputId}>Link URL</Label>
            <Input
              id={linkInputId}
              placeholder="https://example.com"
              value={linkValue}
              onChange={(event) => setLinkValue(event.target.value)}
            />
            <Typography variant="label" tone="muted">
              Supports `https://`, `http://`, `mailto:`, and `tel:`.
            </Typography>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!formats.linkUrl}
              onClick={() => {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
                setLinkValue('')
                setIsLinkPopoverOpen(false)
              }}
            >
              <Unlink />
              Remove
            </Button>
            <Button
              size="sm"
              onClick={() => {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkValue.trim() || null)
                setIsLinkPopoverOpen(false)
              }}
            >
              Apply Link
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

type EditorSyncRefs = {
  committedSnapshotRef: RefObject<RichTextInputSnapshot>
  syncedHtmlRef: RefObject<string | null>
  isApplyingExternalHtmlRef: RefObject<boolean>
}

function ExternalStatePlugin({
  snapshot,
  committedSnapshotRef,
  syncedHtmlRef,
  isApplyingExternalHtmlRef,
}: EditorSyncRefs & { snapshot: RichTextInputSnapshot }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    committedSnapshotRef.current = snapshot
    const nextHtml = snapshot.editorHtmlValue

    if (syncedHtmlRef.current === nextHtml) {
      return
    }

    withExternalEditorSync(isApplyingExternalHtmlRef, () => {
      syncedHtmlRef.current = nextHtml
      replaceEditorHtml(editor, nextHtml)
    })
  }, [committedSnapshotRef, editor, isApplyingExternalHtmlRef, snapshot, syncedHtmlRef])

  return null
}

function EditorChangePlugin({
  onChange,
  preserveSpaces,
  committedSnapshotRef,
  syncedHtmlRef,
  isApplyingExternalHtmlRef,
}: EditorSyncRefs & { onChange: (change: RichTextInputChange) => void; preserveSpaces: boolean }) {
  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={(editorState, editor) => {
        if (isApplyingExternalHtmlRef.current) {
          return
        }

        let generatedHtml: string | null = null

        editorState.read(() => {
          generatedHtml = $generateHtmlFromNodes(editor, null)
        })

        const change = resolveRichTextInputChange({
          committedSnapshot: committedSnapshotRef.current,
          generatedHtml,
          preserveSpaces,
        })

        onChange(change)

        if (
          shouldResyncEditorAfterSanitization({
            generatedHtml,
            normalizedHtml: change.nextSnapshot.editorHtmlValue,
            isApplyingExternalHtml: isApplyingExternalHtmlRef.current,
            preserveSpaces,
          })
        ) {
          withExternalEditorSync(isApplyingExternalHtmlRef, () => {
            syncedHtmlRef.current = change.nextSnapshot.editorHtmlValue
            replaceEditorHtml(editor, change.nextSnapshot.editorHtmlValue)
          })
          return
        }

        syncedHtmlRef.current = change.nextSnapshot.editorHtmlValue
      }}
    />
  )
}

type LexicalRichTextEditorProps = {
  snapshot: RichTextInputSnapshot
  preserveSpaces: boolean
  placeholder: ReactNode
  inputId: string
  label: ReactNode
  labelId: string | undefined
  onBlur: () => void
  onChange: (change: RichTextInputChange) => void
  committedSnapshotRef: RefObject<RichTextInputSnapshot>
}

function LexicalRichTextEditor({
  snapshot,
  preserveSpaces,
  placeholder,
  inputId,
  label,
  labelId,
  onBlur,
  onChange,
  committedSnapshotRef,
}: LexicalRichTextEditorProps) {
  const syncedHtmlRef = useRef<string | null>(null)
  const isApplyingExternalHtmlRef = useRef(false)

  return (
    <LexicalComposer
      initialConfig={{
        namespace: `rich-text-${inputId}`,
        theme: editorTheme,
        nodes: [LinkNode, ListNode, ListItemNode],
        onError(error: Error) {
          throw error
        },
      }}
    >
      <div className="overflow-hidden rounded-md border border-input">
        <RichTextToolbar inputId={inputId} />
        <div className="relative bg-card dark:bg-input/30">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id={inputId}
                aria-labelledby={labelId}
                aria-label={typeof label === 'string' ? label : undefined}
                className="min-h-36 px-3 py-3 text-sm leading-6 outline-none"
                onBlur={onBlur}
              />
            }
            placeholder={
              <div className="pointer-events-none absolute left-3 top-3 text-sm text-placeholder-foreground">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <ExternalStatePlugin
            snapshot={snapshot}
            committedSnapshotRef={committedSnapshotRef}
            syncedHtmlRef={syncedHtmlRef}
            isApplyingExternalHtmlRef={isApplyingExternalHtmlRef}
          />
          <EditorChangePlugin
            onChange={onChange}
            preserveSpaces={preserveSpaces}
            committedSnapshotRef={committedSnapshotRef}
            syncedHtmlRef={syncedHtmlRef}
            isApplyingExternalHtmlRef={isApplyingExternalHtmlRef}
          />
        </div>
      </div>
    </LexicalComposer>
  )
}

type ReadOnlyRichTextEditorShellProps = {
  html: string | null
  fallbackText: string | null
  preserveSpaces: boolean
  inputId: string
  label: ReactNode
  labelId: string | undefined
  className: string | undefined
  readOnlyEmptyText: ReactNode
}

function ReadOnlyRichTextEditorShell({
  html,
  fallbackText,
  preserveSpaces,
  inputId,
  label,
  labelId,
  className,
  readOnlyEmptyText,
}: ReadOnlyRichTextEditorShellProps) {
  const hasContent = Boolean(
    normalizeRichTextHtml(html, { preserveSpaces }) ||
    plainTextToRichTextHtml(fallbackText, { preserveSpaces })
  )

  return (
    <div
      className="overflow-hidden rounded-md border border-input bg-muted/25"
      aria-disabled="true"
    >
      <ReadOnlyRichTextToolbar />
      <div className="bg-muted/15 dark:bg-input/20">
        {hasContent ? (
          <div
            id={inputId}
            role="textbox"
            aria-readonly="true"
            aria-disabled="true"
            aria-labelledby={labelId}
            className="cursor-not-allowed"
          >
            <RichTextContent
              html={html}
              fallbackText={fallbackText}
              preserveSpaces={preserveSpaces}
              className={cn(
                'min-h-36 px-3 py-3 text-muted-foreground selection:bg-transparent',
                className
              )}
            />
          </div>
        ) : (
          <div
            id={inputId}
            role="textbox"
            aria-readonly="true"
            aria-disabled="true"
            aria-label={typeof label === 'string' ? label : undefined}
            aria-labelledby={labelId}
            className="min-h-36 cursor-not-allowed px-3 py-3 text-sm text-muted-foreground"
          >
            {readOnlyEmptyText}
          </div>
        )}
      </div>
    </div>
  )
}

export type RichTextInputProps = {
  id?: string
  label?: ReactNode
  type?: string
  labelEnd?: ReactNode
  labelRowClassName?: string
  onBlur?: () => void
  placeholder?: ReactNode
  className?: string
  plainTextFieldName?: string | null
  readOnlyEmptyText?: ReactNode
  editorReadOnly?: boolean
  readOnlyHtmlValue?: string | null
  readOnlyPlainTextValue?: string | null
  fallbackHtmlValue?: string | null
  fallbackPlainTextValue?: string | null
  /** Keeps the runs of spaces florists type to line recipes up as grids. */
  preserveSpaces?: boolean
}

export default function RichTextInput({
  id,
  label,
  type,
  labelEnd,
  labelRowClassName = '',
  onBlur,
  placeholder = 'Write a description...',
  className,
  plainTextFieldName = null,
  readOnlyEmptyText = 'No description provided',
  editorReadOnly = false,
  readOnlyHtmlValue = undefined,
  readOnlyPlainTextValue = undefined,
  fallbackHtmlValue = undefined,
  fallbackPlainTextValue = undefined,
  preserveSpaces = false,
}: RichTextInputProps) {
  const field = useFieldContext<string | null>()
  // The field's own form is the form an AppField provides context for, and its
  // `getFieldValue`/`setFieldValue` accept the dynamic plain-text field name.
  const form = field.form
  const { isReadOnly } = useIsReadOnly()
  const generatedId = useId()
  const inputId = id || generatedId
  const labelId = `${inputId}-label`
  const plainTextValue: unknown = plainTextFieldName ? form.getFieldValue(plainTextFieldName) : null
  const resolvedHtmlValue =
    editorReadOnly && typeof readOnlyHtmlValue !== 'undefined'
      ? readOnlyHtmlValue
      : field.state.value
  const resolvedPlainTextValue =
    editorReadOnly && typeof readOnlyPlainTextValue !== 'undefined'
      ? readOnlyPlainTextValue
      : plainTextValue
  const snapshot = buildRichTextInputSnapshot({
    htmlValue: resolvedHtmlValue,
    plainTextValue: resolvedPlainTextValue,
    fallbackHtmlValue,
    fallbackPlainTextValue,
    preserveSpaces,
  })
  const committedSnapshotRef = useRef(snapshot)
  const editorHtml = snapshot.editorHtmlValue || null
  // Fallback text only renders through `plainTextToRichTextHtml`, which ignores non-strings.
  const fallbackText = typeof resolvedPlainTextValue === 'string' ? resolvedPlainTextValue : null

  return (
    <div className="flex w-full flex-col gap-y-2">
      {!!label && (
        <div className={cn('inline-flex items-center', labelRowClassName)}>
          <Label id={labelId}>{label}</Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly && !editorReadOnly ? (
        editorHtml ? (
          <RichTextContent
            html={editorHtml}
            fallbackText={fallbackText}
            preserveSpaces={preserveSpaces}
            className={className}
          />
        ) : (
          <Typography variant="supporting">{readOnlyEmptyText}</Typography>
        )
      ) : editorReadOnly ? (
        <ReadOnlyRichTextEditorShell
          html={editorHtml}
          fallbackText={fallbackText}
          preserveSpaces={preserveSpaces}
          inputId={inputId}
          label={label}
          labelId={label ? labelId : undefined}
          className={className}
          readOnlyEmptyText={readOnlyEmptyText}
        />
      ) : (
        <LexicalRichTextEditor
          snapshot={snapshot}
          preserveSpaces={preserveSpaces}
          inputId={inputId}
          label={label}
          labelId={label ? labelId : undefined}
          placeholder={placeholder}
          committedSnapshotRef={committedSnapshotRef}
          onBlur={() => {
            field.handleBlur()
            onBlur?.()
          }}
          onChange={({
            nextSnapshot,
            shouldUpdateHtml,
            nextHtmlValue,
            shouldUpdatePlainText,
            nextPlainTextValue,
          }) => {
            committedSnapshotRef.current = nextSnapshot

            if (shouldUpdateHtml) {
              field.handleChange(nextHtmlValue)
            }

            if (plainTextFieldName && shouldUpdatePlainText) {
              form.setFieldValue(plainTextFieldName, nextPlainTextValue)
            }
          }}
        />
      )}

      {type !== 'hidden' && <FieldInfo />}
    </div>
  )
}
